import { isIbanFormat, validateGermanIbanOffline as validateGermanIbanOfflineUtil } from '@/helper/iban';
import { validateIban } from '@/helper/joules';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useDebounce from './useDebounce';

export type Bank = {
  iban: string;
  bic: string;
  bank: string;
} | null;

export type BankService = {
  isLoading: boolean;
  isValidateLoading: boolean;
  error: string;
  validate: (iban: string | undefined) => Promise<boolean>;
  validateGermanIbanOffline: (iban: string) => boolean;
  getBank: (iban: string) => Promise<Bank | false>;
};

export function useBankService(): BankService {
  const [isLoading, setIsLoading] = useState(false);
  const [isValidateLoading, setIsValidateLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const { t } = useTranslation();

  // Store the last validated IBAN and its result
  const lastValidatedIbanRef = useRef<string | null>(null);
  const isLastIbanValidRef = useRef<boolean>(false);

  const debouncedValidate = useDebounce(async (iban: string, resolve: (value: boolean) => void) => {
    setError('');
    setIsLoading(true);

    try {
      const result = await validateIban(iban);
      const isValid = !!(result && result.bic && result.bank);

      // Update the cache with the new IBAN and its validation result
      lastValidatedIbanRef.current = iban;
      isLastIbanValidRef.current = isValid;

      setIsLoading(false);
      resolve(isValid);
    } catch (error: any) {
      setError(t('Der IBAN-Check ist momentan nicht verfügbar. Bitte versuchen Sie es später erneut.'));
      setIsLoading(false);
      resolve(false);
    }
  }, 500);

  const validate = (iban: string | undefined): Promise<boolean> => {
    if (!iban || !isIbanFormat(iban)) {
      setError('');
      return Promise.resolve(false);
    }

    // Check if the IBAN is the same as the last validated one
    if (iban === lastValidatedIbanRef.current) {
      return Promise.resolve(isLastIbanValidRef.current); // Return the cached result if IBAN hasn't changed
    }

    return new Promise((resolve) => {
      debouncedValidate(iban, resolve);
    });
  };

  const debouncedGetBank = useDebounce(async (iban: string, resolve: (value: Bank | false) => void) => {
    setError('');
    setIsLoading(true);

    try {
      const result = await validateIban(iban);
      const bankObj =
        result && result.bank && result.bic
          ? { iban: result.iban ?? iban, bic: result.bic!, bank: result.bank! }
          : null;

      // Update the cache with the new IBAN and its validation result
      lastValidatedIbanRef.current = iban;
      isLastIbanValidRef.current = !!bankObj;

      setIsLoading(false);
      resolve(bankObj ?? false);
    } catch {
      setError(t('Der IBAN-Check ist momentan nicht verfügbar. Bitte versuchen Sie es später erneut.'));
      setIsLoading(false);
      resolve(false);
    }
  }, 500);

  const getBank = (iban: string): Promise<Bank | false> => {
    if (!iban || !isIbanFormat(iban)) {
      setError('');
      return Promise.resolve(false);
    }

    return new Promise((resolve) => {
      debouncedGetBank(iban, resolve);
    });
  };

  return {
    error,
    isLoading,
    isValidateLoading,
    validate,
    validateGermanIbanOffline: validateGermanIbanOfflineUtil,
    getBank,
  };
}
